import React from 'react'
import clsx from 'clsx'
import { useAuth0 } from '@auth0/auth0-react'
import { useAppSelector } from '../../../hooks/redux'
import { useHistory } from 'react-router'
import { ArrowDown2, HambergerMenu, LogoutCurve, Profile } from 'iconsax-react'
import { Box, Drawer, useTheme } from '@mui/material'

import CloseIcon from './icons/CloseIcon'
import MediaAvatar from '../../atoms/MediaAvatar'
import HamburgerMenuListItem from '../../atoms/HamburgerMenuListItem'
import NotificationBar from '../NotificationBar'

import { Routes } from '../../../Routes'
import { ACCOUNT_KEY } from '../../../providers/AccountProvider'
import { getAllOptions } from '../../organisms/SideNav/helpers'
import { useEnvironment } from '../../../providers/EnvironmentProvider'
import { selectRole } from '../../../slices/role'
import { getPushSubscription } from '../../../setupWorker'
import { unsubscribePush } from '../../../lib/api'
import { useNotificationCount } from '../../../providers/NotificationCountProvider'
import useAccess from '../../../hooks/useAccess'
import useStyles from './styles'

interface HamburgerMenuV2Props {
  open: boolean
  setOpen: (open: boolean) => void
  className?: string
}

const HamburgerMenuV2: React.FC<HamburgerMenuV2Props> = ({
  open,
  setOpen,
  className
}) => {
  const theme = useTheme()
  const history = useHistory()

  const { dashboardNotifs, peerNotifs } = useNotificationCount()
  const { logout, isLoading } = useAuth0()
  const { roles } = useAppSelector(selectRole)
  const { user } = useEnvironment()
  const hasPermission = useAccess()
  const [submenuOpen, setSubmenuOpen] = React.useState(false)
  const navOptions = React.useMemo(
    () => getAllOptions(hasPermission),
    [hasPermission]
  )
  let path = history.location.pathname
  let myProfile = false
  if (path.endsWith('/') && path !== '/') {
    path = path.substring(0, path.length - 1)
  }
  if (path !== '/') {
    const parts = path.split('/').filter(Boolean)
    if (Routes.USERS.includes(parts[0]) && parts[1] === user?.id) {
      myProfile = true
    }
    path = `/${parts[0]}`
  }

  const classes = useStyles({ expanded: submenuOpen })

  const toggleDrawer = React.useCallback(
    toggle => event => {
      const { type, key } = event

      if (type === 'keydown' && ['Tab', 'Shift'].includes(key)) {
        return
      }

      setOpen(toggle)
    },
    [setOpen]
  )

  const redirect = React.useCallback(
    option => {
      const { link, onMove, onClick } = option

      onClick && onClick()

      if (typeof link === 'string') {
        onMove && onMove()
        history.push(link)
      }
      setOpen(false)
    },
    [history, setOpen]
  )

  const handleMyProfileClick = React.useCallback(() => {
    history.push(`${Routes.USERS}/${user?.id}`)
  }, [history, user])

  const handleLogout = React.useCallback(async () => {
    const subscription = await getPushSubscription()
    if (subscription) {
      await subscription.unsubscribe()
      await unsubscribePush(subscription)
    }

    if (!isLoading) {
      localStorage.removeItem('session_user')
      localStorage.removeItem('session_token')
      localStorage.removeItem('version')
      localStorage.removeItem(ACCOUNT_KEY)
      logout({ logoutParams: { returnTo: `${window.location.origin}/login` } })
    }
  }, [isLoading, logout])

  const handleSubmenuToggle = React.useCallback(() => {
    setSubmenuOpen(old => !old)
  }, [])

  return (
    <div data-testid='hamburger-menu-v2'>
      {!open && (
        <HambergerMenu
          variant='Linear'
          size={theme.size.xl}
          onClick={toggleDrawer(true)}
          className={clsx(classes.icon, className)}
        />
      )}

      {open && (
        <Drawer
          anchor='left'
          open={open}
          onClose={toggleDrawer(false)}
          style={{ width: '100vw', zIndex: 99999999 }}
          PaperProps={{
            style: {
              width: '100%',
              padding: 24,
              boxSizing: 'border-box',
              position: 'relative'
            }
          }}
        >
          <Box role='presentation'>
            <div className={classes.myInfoContainer}>
              <MediaAvatar
                user={user}
                hideCard={true}
                className={classes.avatar}
                onClick={handleSubmenuToggle}
              />
              <div
                className={classes.myInfoDetailsColumn}
                onClick={handleSubmenuToggle}
              >
                <label>{user?.name || '...'}</label>
                <label>{roles[user?.role_id]?.name || '...'}</label>
              </div>
              <ArrowDown2
                size='16'
                variant='Bold'
                className={classes.expand}
                onClick={handleSubmenuToggle}
                data-testid={`hamburger-menu-item-expand`}
              />
              <NotificationBar
                className={classes.push}
                fullHeigth
                useSmallDot
              />

              <CloseIcon
                className={classes.close}
                onClick={() => setOpen(false)}
                data-testid={`hamburger-menu-item-close`}
              />
            </div>
            {submenuOpen && (
              <div className={classes.mySubInfoContainer}>
                <ul className={classes.unorderedList}>
                  <HamburgerMenuListItem
                    text='My Profile'
                    icon={<Profile variant='Bold' size={theme.size.xl} />}
                    testid='hamburger-menu-item-my-profile'
                    onClick={handleMyProfileClick}
                    selected={myProfile}
                  />
                  <HamburgerMenuListItem
                    text='Log out'
                    icon={<LogoutCurve variant='Bold' size={theme.size.xl} />}
                    testid='hamburger-menu-item-logout'
                    onClick={handleLogout}
                  />
                </ul>
              </div>
            )}
            <div className={classes.navigationSeparator}></div>
            <div className={classes.navigationContainer}>
              <ul className={classes.unorderedList}>
                {navOptions.map(option => (
                  <HamburgerMenuListItem
                    selected={
                      option.link === path || option.aliases.includes(path)
                    }
                    notifications={
                      option.link === Routes.CLIENT_CHATS
                        ? dashboardNotifs
                        : option.link === Routes.PEER_CHAT
                        ? peerNotifs
                        : 0
                    }
                    key={option.name}
                    text={option.name}
                    icon={option.Content()}
                    onClick={() => redirect(option)}
                    testid={`hamburger-menu-item-${option.testId}`}
                  />
                ))}
              </ul>
            </div>
          </Box>
          <div className={classes.footer}>
            <img
              src='/imgs/sidenavV2logo.png'
              alt='SonarCX logo'
              className={classes.footerLogo}
            />
            <label>SonarCX</label>
          </div>
        </Drawer>
      )}
    </div>
  )
}

export default HamburgerMenuV2
